import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, QueryList, ViewChildren, AfterViewInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import Icons from 'src/app/core/enums/icons.enum';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { SecondaryToolbar } from 'src/portaljs/models/secondary-toolbar';
import * as _ from 'lodash-es';
import { BreadcrumbItem, ButtonItem, MenuItem, SelectItem } from 'src/portaljs/models/menu-item';
import { Message } from 'src/portaljs/models/message';
import { Application } from 'src/portaljs/constants/application';
import { MessageType } from 'src/portaljs/constants/message-type';
import { DestinyViewType } from 'src/portaljs/constants/destiny-view-type';
import { ClientStateService } from 'src/app/core/services/client-state.service';
import { MatMenuPanel } from '@angular/material/menu';
import { NewMenuItemComponent } from '../sidenav/menu.component';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { IconsService } from 'src/app/core/services/icons.service';


@Component({
  selector: 'fss-portal-secondary-toolbar',
  templateUrl: './secondary-toolbar.component.html',
  styleUrls: ['./secondary-toolbar.component.scss'],
  standalone: false
})
export class SecondaryToolbarComponent implements OnDestroy, OnInit, AfterViewInit {
  private ngUnsubscribe = new Subject();
  public icons = Icons;
  public secondaryToolbar: SecondaryToolbar;
  public breadcrumbWidth: number = 0;
  public breadcrumbs: BreadcrumbItem[] = [];
  public truncate: boolean = false; // Whether to truncate breadcrumbs
  @ViewChildren(NewMenuItemComponent) matMenus: QueryList<NewMenuItemComponent>;
  @ViewChildren('breadcrumbItem') breadcrumbElements: QueryList<any>;

  constructor(
    private portalStateService: PortalStateService,
    private clientStateService: ClientStateService,
    private toolbarService: ToolbarService,
    public iconService: IconsService,
    private cdr: ChangeDetectorRef
  ) {
  }

  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public getMatMenu(id: string): MatMenuPanel {
    if (this.matMenus) {
      const menuComponent = this.matMenus.find(menu => menu.menu.id === id);
      return menuComponent ? menuComponent.matmenus.first : null;
    }
    return undefined;
  }

  public isDDorCollections() {
    return this.portalStateService.getActiveApp().app === Application.DD || this.portalStateService.getActiveApp().app === Application.COLLECTIONS;
  }

  public ngAfterViewInit() {
    if (this.breadcrumbElements) {
      this.breadcrumbElements.changes
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.calculateBreadcrumbWidth());
    }
  }

  public ngOnInit(): void {
    this.portalStateService.getActiveSecondaryToolbar$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((secondaryToolbar: SecondaryToolbar) => {
        this.secondaryToolbar = _.cloneDeep(secondaryToolbar);
        if (this.portalStateService.getActiveApp().app === Application.DESTINY || this.portalStateService.getActiveApp().app === Application.DESTINY_UI) {
          const buttonItems = this.secondaryToolbar?.buttonItems;
          if (buttonItems && buttonItems.length > 0) {
            this.secondaryToolbar.buttonItems = buttonItems.filter(item => !item.name.includes('Reset'));
          }
        }
        //this ^^^ code is a HACK for LCP-392 to make the DBO reset button show in the sidenav instead of the secondary toolbar
        this.breadcrumbs = this.secondaryToolbar?.breadcrumbItems || [];
        this.truncate = false; // reset truncate to force full breadcrumb display
        this.breadcrumbWidth = 0; // reset width to force re-measurement
        // Force change detection to render breadcrumbs, then measure
        this.cdr.detectChanges();
        this.calculateBreadcrumbWidth();
      });
  }

  public getButtons(): BreadcrumbItem[] {
    return this.secondaryToolbar?.buttonItems || [];
  }
  public getSelects(): BreadcrumbItem[] {
    return (this.secondaryToolbar?.selectItems || []).filter(s => s.subMenuItems.length > 0);
  }

  public isVisible(): boolean {
    return !_.isEmpty(this.secondaryToolbar?.breadcrumbItems) || !_.isEmpty(this.secondaryToolbar?.buttonItems) || !_.isEmpty(this.secondaryToolbar?.selectItems);
  }

  public calculateBreadcrumbWidth() {
    if (this.breadcrumbElements && this.breadcrumbElements.length > 0) {
      this.breadcrumbWidth = 0;
      this.breadcrumbElements.forEach(breadcrumb => {
        this.breadcrumbWidth += breadcrumb.nativeElement.scrollWidth;
      });
    }

    if (this.breadcrumbs?.length >= 3) {
      const breadcrumbContainer = document.getElementById('breadcrumb-container');
      if (breadcrumbContainer && this.breadcrumbWidth > 0) {
        this.truncate = this.breadcrumbWidth > breadcrumbContainer.clientWidth;
      }
    } else {
      this.truncate = false;
    }
  }

  @HostListener('window:resize')
  public onResize(): void {
    if (this.breadcrumbs?.length >= 3) {
      const breadcrumbContainer = document.getElementById('breadcrumb-container');
      if (breadcrumbContainer && this.breadcrumbWidth > 0) {
        this.truncate = this.breadcrumbWidth > breadcrumbContainer.clientWidth;
      }
    }
    this.cdr.detectChanges();
  }

  public shouldTruncateBreadcrumbs(): boolean {
    return this.truncate;
  }

  public itemClicked(item: ButtonItem | BreadcrumbItem): void {
    const id = item.id;
    const msg = new Message(id, MessageType.secondaryToolbarSelect, Application.PORTAL, this.portalStateService.getActiveApp().app, 'clicked');
    this.portalStateService.postMessage(msg);
  }


  public getSelectDisplayValue(select: SelectItem): MenuItem {
    const selectedItem = this.findNestedSubMenuItem(select.subMenuItems, (item) => item.default);
    return selectedItem ? selectedItem : select;
  }


  public itemOptionSelected(selectedParent: SelectItem, selectedItem: BreadcrumbItem): void {
    this.clearDefaultOnAllSubMenuItems(selectedParent);
    selectedItem.default = true;
    this.clientStateService.refreshSecondaryToolbarSelectValues(this.portalStateService.getActiveApp().app, selectedParent);
    this.portalStateService.setActiveSecondaryToolbar();

    if (selectedParent?.id === 'legacy-secondary-destinyViewType') {
      this.portalStateService.sendDestinyViewTypeChange(selectedItem.id as DestinyViewType);
    } else {
      const msg = new Message(selectedItem.id, MessageType.secondaryToolbarSelect, Application.PORTAL, this.portalStateService.getActiveApp().app, 'clicked');
      this.portalStateService.postMessage(msg);
    }
  }


  private findNestedSubMenuItem(subMenuItems: MenuItem[] = [], comparator: (item: MenuItem) => boolean): MenuItem {
    for (const item of subMenuItems) {
      if (comparator(item)) {
        return item;
      }
      if (item.subMenuItems) {
        const foundItem = this.findNestedSubMenuItem(item.subMenuItems, comparator);
        if (foundItem) {
          return foundItem;
        }
      }
    }
    return null;
  }


  private clearDefaultOnAllSubMenuItems(select: SelectItem): void {
    for (const item of select.subMenuItems) {
      item.default = false;
      if (item.subMenuItems) {
        this.clearDefaultOnAllSubMenuItems(item);
      }
    }
  }
}
